import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, movies, siteInfo } from "@/lib/movieData";

const FORM_ID = "feedback-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db9lfo46oi3g3tv8v0g0";

const values = [
  {
    icon: "ri-eye-line",
    title: "信息可核",
    text: "片名、年份、主创与上映信息统一校对，列表与详情保持一致，方便影迷快速确认。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "结构清晰",
    text: "以类型、地区、年份构建检索维度，让查找电影资料像翻目录一样直接。",
  },
  {
    icon: "ri-user-heart-line",
    title: "面向观众",
    text: "不堆砌专业术语，用可读的文字呈现剧情与看点，帮助普通观众做观影选择。",
  },
];

const notices = [
  {
    title: "版权声明",
    text: "站内电影名称、海报与简介为演示用虚构资料，仅用于产品展示，不构成任何实际影片的授权或发行信息。如涉及真实作品，请以官方发布为准。",
  },
  {
    title: "用户须知",
    text: "本站不提供在线播放、下载或资源链接，页面中的「分享」仅用于传播当前页面地址。站内资讯为演示内容，不构成观影或消费建议。",
  },
  {
    title: "数据说明",
    text: "演示阶段所有内容为本地示例数据，刷新页面不会丢失。后续如需长期运营与内容管理，可接入后端数据库与内容后台。",
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  usePageMeta({
    title: "关于我们 - 秋霞电影",
    description:
      "了解秋霞电影：一个专注电影资讯与影视内容聚合的平台，介绍站点定位、内容范围、版权声明与观众反馈渠道。",
    keywords: "关于秋霞电影,电影资讯平台,影视内容聚合,网站介绍,观众反馈",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = typeof value === "string" ? value : "";
      if (text.trim()) payload.append(key, text);
    });

    setFormStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (!ok) {
        setFormStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
        return;
      }

      setFormStatus("success");
      setMessage("");
      form.reset();
    } catch {
      setFormStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <PageLayout>
      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">关于</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 items-end">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-foreground-400 uppercase">About</p>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                关于秋霞电影
              </h1>
              <p className="mt-4 max-w-[620px] text-sm md:text-base leading-relaxed text-foreground-600">
                {siteInfo.description}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/drama"
                  className="h-11 px-6 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  浏览片库
                </Link>
                <a
                  href="#feedback"
                  className="h-11 px-6 inline-flex items-center gap-2 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-chat-3-line"></i>
                  意见反馈
                </a>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-px rounded-lg overflow-hidden border border-background-200 bg-background-200">
              {[
                { value: `${movies.length}`, label: "影片资料" },
                { value: `${categories.length}`, label: "影片类型" },
                { value: siteInfo.since, label: "开始运营" },
              ].map((item) => (
                <div key={item.label} className="bg-background-50 px-4 py-6 text-center">
                  <p className="font-heading text-xl md:text-2xl text-primary-600">{item.value}</p>
                  <p className="mt-2 text-[11px] text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950">我们在做什么</h2>
              <div className="mt-5 space-y-4 text-sm md:text-[15px] leading-[1.9] text-foreground-700">
                <p>
                  秋霞电影是一个以电影资讯与影视内容聚合为核心的平台。我们把散落在各处的定档消息、主创访谈、口碑动态整理成结构化的页面，让影迷打开网站就能掌握最近发生了什么。
                </p>
                <p>
                  平台按类型、地区与年份建立索引，每部影片都有独立的资料页，包含海报、主创、剧情概述与相关资讯。列表页与详情页使用同一份数据，确保标题、封面与简介始终一致。
                </p>
                <p>
                  我们相信，好的内容平台不需要制造焦虑。清楚的分类、可读的文字和稳定的浏览节奏，就是影迷真正需要的东西。
                </p>
              </div>

              <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {values.map((value) => (
                  <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${value.icon} text-base`}></i>
                    </span>
                    <h3 className="mt-4 text-sm font-medium text-foreground-900">{value.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{value.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg text-foreground-950">内容范围</h2>
                <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-600 mt-0.5"></i>
                    最新电影资讯与上映动态
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-600 mt-0.5"></i>
                    类型化片单与热度榜单
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-600 mt-0.5"></i>
                    影片资料、剧情概述与主创信息
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-foreground-400 mt-0.5"></i>
                    在线播放、下载与资源分发
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-primary-500 p-6 text-background-50">
                <h2 className="font-heading text-lg">联系我们</h2>
                <p className="mt-2 text-xs leading-relaxed text-primary-100/80">
                  内容纠错、资料补充或合作咨询，欢迎通过邮件联系编辑部。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-4 flex items-center gap-2 text-sm text-accent-300 hover:text-accent-200 transition-colors break-all"
                >
                  <i className="ri-mail-line shrink-0"></i>
                  {siteInfo.email}
                </a>
                <p className="mt-3 text-[11px] text-primary-200/70">通常在 2 个工作日内回复</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 border-y border-background-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="font-heading text-2xl text-foreground-950">声明与须知</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {notices.map((notice) => (
              <div key={notice.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="text-sm font-medium text-foreground-900 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                  {notice.title}
                </h3>
                <p className="mt-3 text-xs leading-relaxed text-foreground-600">{notice.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="feedback" className="w-full bg-background-100 scroll-mt-24">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-foreground-400 uppercase">Feedback</p>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">意见反馈</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                发现资料错误、想补充某部影片的资讯，或者对站点的分类方式有建议？填写下面的表单，我们会认真阅读每一条反馈。
              </p>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-xs text-foreground-500">也可以直接发邮件</p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-2 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-accent-600 transition-colors break-all"
                >
                  <i className="ri-mail-send-line"></i>
                  {siteInfo.email}
                </a>
              </div>
              <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
                提示：反馈表单为站点演示功能，提交内容会安全送达表单服务，不会用于其他用途。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <form
                id={FORM_ID}
                data-readdy-form
                action={SUBMIT_ADDR}
                method="post"
                onSubmit={handleSubmit}
                className="feedback-form flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fb-name" className="block text-xs text-foreground-600 mb-1.5">
                      称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="block text-xs text-foreground-600 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于接收回复"
                      className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-topic" className="block text-xs text-foreground-600 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="fb-topic"
                    name="topic"
                    defaultValue="内容纠错"
                    className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="资料补充">影片资料补充</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="fb-message" className="block text-xs text-foreground-600">
                      反馈内容
                    </label>
                    <span className="text-[11px] text-foreground-400">{message.length} / 500</span>
                  </div>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={message}
                    onChange={(event) => setMessage(event.target.value.slice(0, 500))}
                    placeholder="请描述具体页面、影片名称或你希望补充的信息"
                    className="w-full px-3 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none leading-relaxed"
                  ></textarea>
                </div>

                <div className="company-alt-field">
                  <label htmlFor="fb-company-alt">公司名称（可选）</label>
                  <input
                    id="fb-company-alt"
                    name="company_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus === "error" && (
                  <div className="rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
                    <p className="text-xs text-accent-800 flex items-start gap-2">
                      <i className="ri-error-warning-line mt-0.5"></i>
                      {formError || "提交失败，请稍后重试。"}
                    </p>
                  </div>
                )}

                {formStatus === "success" && (
                  <div className="rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3">
                    <p className="text-xs text-secondary-900 flex items-start gap-2">
                      <i className="ri-checkbox-circle-line mt-0.5"></i>
                      反馈已提交，感谢你的帮助。我们会尽快核对相关内容。
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  <p className="text-[11px] text-foreground-400">
                    提交即表示同意我们仅在反馈处理中使用这些信息。
                  </p>
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:pointer-events-none whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        提交反馈
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}